import { useMemo, useState } from 'react';

import {
  DeltaAction,
  DeltaStatus,
  DocumentKind,
  type DeltaResponse,
  type DocumentResponse,
  type InsuredOnDateResponse,
} from '../../api';
import {
  AsyncSection,
  DataTable,
  FileField,
  Form,
  FormError,
  PageHeader,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  useNotifications,
  type Column,
} from '../../components';
import {
  formatCpf,
  formatCpfWhileTyping,
  formatCurrency,
  formatDate,
  formatDateTime,
  todayInAppTimeZone,
} from '../../format';
import {
  useCancelChange,
  useCompanyDocuments,
  useDeltaHistory,
  usePendingDeltas,
  usePortalContracts,
  useRoster,
  useSubmitChange,
} from '../queries';
import { useCompanyScope } from './companyScope';
import {
  ACTION_DESCRIPTIONS,
  ACTION_LABELS,
  EFFECTIVE_DATE_HINTS,
  PORTAL_COPY,
  STATUS_LABELS,
  changeFieldErrors,
  changeFormMessage,
  documentsByDelta,
  nameForChange,
} from './portal';
import {
  EMPTY_CHANGE_FORM,
  actsOnExistingPerson,
  collectsDocument,
  collectsIdentity,
  collectsLeaveEndDate,
  collectsSalary,
  validateChange,
  type ChangeFormErrors,
  type ChangeFormState,
} from './changeForm';

/**
 * Asking for a change, and seeing what has been asked for.
 *
 * The form is one form with an action at the top. The insurer's file expresses
 * a movement as three mutually exclusive columns, and reproducing that here --
 * three panels, or three sets of fields side by side -- is how a person ends up
 * filling in two of them. A single selector cannot express two actions, and the
 * body it produces is one of the API's four shapes, each of which forbids the
 * others' fields (#210, #284). The fourth, an afastamento, has no column in that
 * file at all, which is another reason the file's layout is not this screen's.
 *
 * Nothing on this screen asks for `subestipulante` or `modulo`. They are terms
 * of the contract, held by #209, and the API has no field for them on a change
 * at all.
 *
 * There is no edit. A pending change is cancelled and asked for again, which is
 * the API's own lifecycle rather than a simplification: an approved change is
 * already in the roster, and editing one would rewrite history.
 */

const ACTION_OPTIONS = [
  { value: DeltaAction.ADMISSION, label: ACTION_DESCRIPTIONS[DeltaAction.ADMISSION] },
  { value: DeltaAction.TERMINATION, label: ACTION_DESCRIPTIONS[DeltaAction.TERMINATION] },
  { value: DeltaAction.SALARY_CHANGE, label: ACTION_DESCRIPTIONS[DeltaAction.SALARY_CHANGE] },
  { value: DeltaAction.LEAVE, label: ACTION_DESCRIPTIONS[DeltaAction.LEAVE] },
] as const;

function personOptions(people: readonly InsuredOnDateResponse[]) {
  return people.map((person) => ({
    value: person.insured_person_id,
    label: `${person.full_name} — ${formatCpf(person.cpf)}`,
  }));
}

/**
 * When somebody on an afastamento comes back, as one cell.
 *
 * Three different things, and they must not read alike: a movement that is not
 * an afastamento has nothing to say here, an afastamento with no end date is
 * *open* rather than blank, and one with an end date names the day. A dash for
 * all three would turn "we do not know when they return" into "this column does
 * not apply", which is the fact the customer files a second request about.
 */
function leaveEndCell(delta: DeltaResponse) {
  if (delta.action !== DeltaAction.LEAVE) {
    return <span className="table__muted">—</span>;
  }
  return delta.leave_end_date !== null && delta.leave_end_date !== undefined ? (
    formatDate(delta.leave_end_date)
  ) : (
    <span className="table__muted">{PORTAL_COPY.leaveOpenEnded}</span>
  );
}

/** What is attached to a change, as one readable cell. */
function attachmentsCell(documents: readonly DocumentResponse[]) {
  if (documents.length === 0) {
    return <span className="table__muted">—</span>;
  }
  return (
    <ul className="attachments">
      {documents.map((document) => (
        <li key={document.id}>{document.original_filename}</li>
      ))}
    </ul>
  );
}

export function ChangesPage() {
  const { company } = useCompanyScope();
  const { notify } = useNotifications();

  const contracts = usePortalContracts(company.id);
  // A customer has one contract today. `noUncheckedIndexedAccess` makes the
  // "or none yet" case something every read below has to handle, which is
  // what keeps a company still being onboarded off a broken screen.
  const contract = contracts.data?.items[0];
  const [asOf] = useState(todayInAppTimeZone);

  const roster = useRoster(company.id, contract?.id, asOf);
  const pending = usePendingDeltas(company.id, contract?.id);
  const history = useDeltaHistory(company.id, contract?.id);
  const documents = useCompanyDocuments(company.id);

  const [form, setForm] = useState<ChangeFormState>(EMPTY_CHANGE_FORM);
  const [localErrors, setLocalErrors] = useState<ChangeFormErrors>({});
  /**
   * Bumped after each accepted submission, and used as the file input's key.
   *
   * A file input's value cannot be set from script, so clearing the form state
   * leaves the previous file's name sitting under the label -- which reads as
   * "this is still attached" to the next termination being filed. Remounting it
   * is the only way to actually empty it.
   */
  const [submissionCount, setSubmissionCount] = useState(0);

  const submit = useSubmitChange();
  const cancel = useCancelChange();

  const people = roster.data?.items ?? [];
  const attachments = useMemo(
    () => documentsByDelta(documents.data?.items ?? []),
    [documents.data],
  );

  // The server's refusal wins over the browser's: it knows about the roster.
  const serverErrors = changeFieldErrors(submit.error);
  const errors: ChangeFormErrors = { ...localErrors, ...serverErrors };

  const update = <TKey extends keyof ChangeFormState>(key: TKey, value: ChangeFormState[TKey]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  /**
   * Changing the action clears the fields the previous one owned.
   *
   * Otherwise a salary typed for an admission travels into a termination that
   * has no salary field, which the API would refuse -- and the person would be
   * refused over something they cannot see on screen.
   */
  const chooseAction = (action: DeltaAction | '') => {
    setForm({ ...EMPTY_CHANGE_FORM, action, effectiveDate: form.effectiveDate });
    setLocalErrors({});
    submit.reset();
  };

  const onSubmit = () => {
    if (contract === undefined) {
      return;
    }
    const outcome = validateChange(form, people);
    if ('errors' in outcome) {
      setLocalErrors(outcome.errors);
      return;
    }
    setLocalErrors({});
    submit.mutate(
      {
        companyId: company.id,
        contractId: contract.id,
        delta: outcome.delta,
        ...(collectsDocument(form.action) && form.file !== null
          ? { document: { file: form.file, kind: DocumentKind.TERMINATION_AGREEMENT } }
          : {}),
      },
      {
        onSuccess: (result) => {
          setForm(EMPTY_CHANGE_FORM);
          setSubmissionCount((count) => count + 1);
          notify('success', PORTAL_COPY.submitted);
          if (result.documentFailed) {
            notify('warning', PORTAL_COPY.documentAttachFailed);
          }
        },
      },
    );
  };

  const pendingColumns: readonly Column<DeltaResponse>[] = [
    { key: 'action', header: 'Movimentação', render: (delta) => ACTION_LABELS[delta.action] },
    {
      key: 'person',
      header: 'Pessoa',
      render: (delta) => nameForChange(delta, people),
    },
    { key: 'cpf', header: 'CPF', render: (delta) => <code>{formatCpf(delta.cpf)}</code> },
    {
      key: 'effective_date',
      header: 'Vigência',
      align: 'end',
      render: (delta) => formatDate(delta.effective_date),
    },
    {
      key: 'salary',
      header: 'Salário',
      align: 'end',
      render: (delta) => formatCurrency(delta.salary),
    },
    {
      key: 'leave_end_date',
      header: 'Volta em',
      align: 'end',
      render: (delta) => leaveEndCell(delta),
    },
    {
      key: 'documents',
      header: 'Documento',
      render: (delta) => attachmentsCell(attachments.get(delta.id) ?? []),
    },
    {
      key: 'submitted_at',
      header: 'Pedido em',
      align: 'end',
      render: (delta) => formatDateTime(delta.submitted_at),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (delta) => (
        <button
          type="button"
          className="button button--danger"
          disabled={cancel.isPending}
          onClick={() =>
            cancel.mutate(
              { companyId: company.id, deltaId: delta.id },
              {
                onSuccess: () => notify('success', PORTAL_COPY.cancelled),
                onError: () => notify('error', PORTAL_COPY.cancelFailed),
              },
            )
          }
        >
          {cancel.isPending ? PORTAL_COPY.cancelling : PORTAL_COPY.cancel}
        </button>
      ),
    },
  ];

  const historyColumns: readonly Column<DeltaResponse>[] = [
    { key: 'action', header: 'Movimentação', render: (delta) => ACTION_LABELS[delta.action] },
    {
      key: 'person',
      header: 'Pessoa',
      render: (delta) => nameForChange(delta, people),
    },
    {
      key: 'effective_date',
      header: 'Vigência',
      align: 'end',
      render: (delta) => formatDate(delta.effective_date),
    },
    {
      key: 'leave_end_date',
      header: 'Volta em',
      align: 'end',
      render: (delta) => leaveEndCell(delta),
    },
    { key: 'status', header: 'Situação', render: (delta) => STATUS_LABELS[delta.status] },
    {
      key: 'documents',
      header: 'Documento',
      render: (delta) => attachmentsCell(attachments.get(delta.id) ?? []),
    },
    {
      key: 'submitted_at',
      header: 'Pedido em',
      align: 'end',
      render: (delta) => formatDateTime(delta.submitted_at),
    },
  ];

  return (
    <div className="page">
      <PageHeader title={PORTAL_COPY.changesTitle} description={PORTAL_COPY.changesDescription} />

      <AsyncSection
        state={contracts}
        loadingLabel="Carregando contrato…"
        errorTitle="Não foi possível carregar o contrato"
        isEmpty={(data) => data.items.length === 0}
        emptyTitle="Nenhum contrato"
        emptyDescription={PORTAL_COPY.noContract}
      >
        {() => (
          <>
            <Panel title={PORTAL_COPY.newChangeTitle}>
              <Form onSubmit={onSubmit}>
                <FormError error={submit.error} message={changeFormMessage(submit.error)} />

                <SelectField
                  label={PORTAL_COPY.actionLabel}
                  hint={PORTAL_COPY.actionHint}
                  placeholder={PORTAL_COPY.actionPlaceholder}
                  options={ACTION_OPTIONS}
                  value={form.action}
                  onChange={chooseAction}
                  error={errors['action']}
                  required
                />

                {form.action !== '' && (
                  <>
                    {actsOnExistingPerson(form.action) && (
                      <SelectField
                        label={PORTAL_COPY.personLabel}
                        hint={PORTAL_COPY.personHint}
                        placeholder={PORTAL_COPY.personPlaceholder}
                        options={personOptions(people)}
                        value={form.personId}
                        onChange={(value) => update('personId', value)}
                        error={errors['person'] ?? errors['cpf']}
                        required
                      />
                    )}

                    {collectsIdentity(form.action) && (
                      <>
                        <TextField
                          label={PORTAL_COPY.cpfLabel}
                          hint={PORTAL_COPY.cpfHint}
                          value={form.cpf}
                          onChange={(value) => update('cpf', formatCpfWhileTyping(value))}
                          error={errors['cpf']}
                          inputMode="numeric"
                          autoComplete="off"
                          required
                        />
                        <TextField
                          label={PORTAL_COPY.fullNameLabel}
                          value={form.fullName}
                          onChange={(value) => update('fullName', value)}
                          error={errors['full_name']}
                          autoComplete="off"
                          required
                        />
                        <TextField
                          label={PORTAL_COPY.birthDateLabel}
                          type="date"
                          value={form.birthDate}
                          onChange={(value) => update('birthDate', value)}
                          error={errors['birth_date']}
                          required
                        />
                      </>
                    )}

                    {/*
                      No `max` on this input, and no warning anywhere near it. A
                      change effective in a future month is the ordinary case --
                      terminations are filed as soon as they are signed -- and
                      the API stores it with that date and applies it when the
                      date arrives.
                    */}
                    <TextField
                      label={PORTAL_COPY.effectiveDateLabel}
                      hint={EFFECTIVE_DATE_HINTS[form.action]}
                      type="date"
                      value={form.effectiveDate}
                      onChange={(value) => update('effectiveDate', value)}
                      error={errors['effective_date']}
                      required
                    />

                    {collectsLeaveEndDate(form.action) && (
                      // Not `required`, and the label says so. An afastamento
                      // whose return date nobody knows yet is the ordinary case,
                      // and a form that insisted on one would be a form people
                      // make a date up on.
                      <TextField
                        label={PORTAL_COPY.leaveEndDateLabel}
                        hint={PORTAL_COPY.leaveEndDateHint}
                        type="date"
                        value={form.leaveEndDate}
                        onChange={(value) => update('leaveEndDate', value)}
                        error={errors['leave_end_date']}
                      />
                    )}

                    {collectsSalary(form.action) && (
                      <TextField
                        label={PORTAL_COPY.salaryLabel}
                        hint={PORTAL_COPY.salaryHint}
                        value={form.salary}
                        onChange={(value) => update('salary', value)}
                        error={errors['salary']}
                        inputMode="numeric"
                        autoComplete="off"
                        required
                      />
                    )}

                    {collectsDocument(form.action) && (
                      <FileField
                        key={submissionCount}
                        label={PORTAL_COPY.documentLabel}
                        hint={PORTAL_COPY.documentHint}
                        accept="application/pdf,image/*"
                        onChange={(file) => update('file', file)}
                      />
                    )}

                    <div className="form__actions">
                      <SubmitButton
                        pending={submit.isPending}
                        pendingLabel={PORTAL_COPY.submitting}
                      >
                        {PORTAL_COPY.submit}
                      </SubmitButton>
                    </div>
                  </>
                )}
              </Form>
            </Panel>

            <Panel title={PORTAL_COPY.pendingTitle} description={PORTAL_COPY.pendingDescription}>
              <AsyncSection
                state={pending}
                loadingLabel="Carregando movimentações pendentes…"
                errorTitle="Não foi possível carregar as movimentações pendentes"
              >
                {(data) => (
                  <DataTable
                    id="customer-pending-changes"
                    caption="Movimentações pedidas e ainda não decididas"
                    columns={pendingColumns}
                    rows={data.items.filter((delta) => delta.status === DeltaStatus.SUBMITTED)}
                    rowKey={(delta) => delta.id}
                    emptyTitle={PORTAL_COPY.noPending}
                  />
                )}
              </AsyncSection>
            </Panel>

            <Panel title={PORTAL_COPY.historyTitle} description={PORTAL_COPY.historyDescription}>
              <AsyncSection
                state={history}
                loadingLabel="Carregando histórico…"
                errorTitle="Não foi possível carregar o histórico"
              >
                {(data) => (
                  <DataTable
                    id="customer-changes"
                    caption="Todas as movimentações já pedidas para este contrato"
                    columns={historyColumns}
                    rows={data.items}
                    rowKey={(delta) => delta.id}
                    emptyTitle={PORTAL_COPY.noHistory}
                  />
                )}
              </AsyncSection>
            </Panel>
          </>
        )}
      </AsyncSection>
    </div>
  );
}
